import { IconButton, makeStyles } from '@material-ui/core';
import {
  WbTextField,
  WbTextFieldProps,
  AccessibilityProps,
} from './WbTextField';
import PublishIcon from '@material-ui/icons/Publish';
import ClearIcon from '@material-ui/icons/Clear';
import clsx from 'clsx';
import {
  ChangeEvent,
  MutableRefObject,
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useRef,
} from 'react';

export type WbIconFilePickerProps = Omit<
  WbTextFieldProps,
  | 'value'
  | 'onChange'
  | 'debounceMs'
  | 'startAdornment'
  | 'endAdornment'
  | 'type'
  | 'id'
  | 'ariaLabel'
  | 'label'
> &
  AccessibilityProps & {
    value: File | undefined;
    onChange: (file: File | undefined) => void;
    iconRender?: (icon: File) => ReactNode;
    id?: string;
    ariaLabel?: string;
    label?: string | React.ReactNode;
  };

const useStyles = makeStyles(
  () => ({
    root: {
      position: 'relative',
    },
    img: {
      width: '16px',
      height: '16px',
      objectFit: 'contain',
    },
    input: {
      position: 'absolute',
      height: '100%',
      left: 0,
      width: '100%',
      opacity: 0,
      padding: 0,
      cursor: 'pointer',
    },
  }),
  { name: 'WbIconFilePicker' },
);

const FileInput = forwardRef(
  ({ value, className, ...props }: any, _ref: Ref<HTMLInputElement>) => {
    const classes = useStyles();
    return (
      <input
        className={clsx(className, classes.input)}
        ref={props.inputRef}
        type="file"
        accept="image/*"
        {...props}
      />
    );
  },
);

export const WbIconFilePicker = forwardRef(
  (props: WbIconFilePickerProps, externalRef?: Ref<HTMLInputElement>) => {
    const {
      value,
      onChange,
      InputProps,
      clearable,
      iconRender,
      id,
      label,
      ariaLabel,
      ...otherProps
    } = props;

    const classes = useStyles();

    // Local ref to store the file input element
    const localRef = useRef<HTMLInputElement | null>(null);

    // Memoized function to merge external and local refs
    const setRefs = useCallback(
      (element: HTMLInputElement | null) => {
        localRef.current = element;
        if (typeof externalRef === 'function') {
          externalRef(element);
        } else if (externalRef) {
          (externalRef as MutableRefObject<HTMLInputElement | null>).current =
            element;
        }
      },
      [externalRef],
    );

    const accessibilityProps =
      id && label
        ? { id, label }
        : { id, ariaLabel: ariaLabel ?? 'select icon' };

    return (
      <WbTextField
        ref={setRefs}
        InputProps={{
          className: classes.root,
          inputComponent: FileInput,
          startAdornment: value
            ? iconRender?.(value) ?? (
                <img
                  src={URL.createObjectURL(value)}
                  className={classes.img}
                  alt={value.name}
                />
              )
            : undefined,
          endAdornment: (
            <>
              <div style={{ width: '100px', visibility: 'hidden' }} />
              {clearable && (
                <IconButton
                  color="inherit"
                  size="small"
                  style={{
                    marginLeft: 'auto',
                    visibility: value ? 'visible' : 'hidden',
                  }}
                  onClick={() => {
                    if (localRef.current) {
                      localRef.current.value = '';
                    }
                    onChange(undefined);
                  }}
                  aria-label="clear"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <PublishIcon
                style={{
                  marginLeft: value ? undefined : 'auto',
                }}
                fontSize="small"
              />
            </>
          ),
          ...InputProps,
        }}
        type="file"
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.files && e.target.files.length > 0) {
            onChange(e.target.files[0]); // Update the field value with the selected file
          } else {
            onChange(undefined); // Reset the field value if no file is selected
          }
        }}
        {...otherProps}
        {...accessibilityProps}
      />
    );
  },
);
